import {
  VStack,
  SimpleGrid,
  Text,
  Box,
  Button,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';

interface Props {
  onCancel: () => void;
}

interface PieChartProps {
  baixo: number;
  moderado: number;
  alto: number;
}

const PieChart = ({ baixo, moderado, alto }: PieChartProps) => {
  const total = baixo + moderado + alto;

  const baixoEnd = baixo;
  const moderadoEnd = baixo + moderado;

  return (
    <Flex direction="column" align="center" mt={6} position="relative">
      <Box position="relative" w="180px" h="180px">
        {/* Pizza */}
        <Box
          w="100%"
          h="100%"
          borderRadius="50%"
          background={`conic-gradient(
            #F56565 0% ${baixoEnd}%,
            #4FD1C5  ${baixoEnd}% ${moderadoEnd}%,
            #ECC94B ${moderadoEnd}% 100%
          )`}
        />

        {/* Labels posicionadas */}
        {/* BAIXO */}
        <Text
          position="absolute"
          bottom="-10px"
          right="-30px"
          fontSize="sm"
          color="teal.400"
        >
          {alto}%
        </Text>

        {/* MODERADO */}
        <Text
          position="absolute"
          left="-40px"
          top="40%"
          fontSize="sm"
          color="yellow.400"
        >
          {moderado}%
        </Text>

        {/* BAIXO */}
        <Text
          position="absolute"
          top="-15px"
          right="10px"
          fontSize="sm"
          color="red.400"
        >
          {baixo}%
        </Text>
      </Box>

      {/* Legenda */}
      <Flex mt={4} gap={4} fontSize="sm">
        <Flex align="center">
          <Box w="10px" h="10px" bg="red.400" mr={2} />
          <Text color="red.400">Baixo (desfavorável)</Text>
        </Flex>

        <Flex align="center">
          <Box w="10px" h="10px" bg="yellow.400" mr={2} />
          <Text color="yellow.400">Moderado</Text>
        </Flex>

        <Flex align="center">
          <Box w="10px" h="10px" bg="teal.400" mr={2} />
          <Text color="teal.400">Alto (favorável)</Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

interface BarItem {
  label: string;
  value: number;
}

interface HorizontalBarChartProps {
  data: BarItem[];
}

export const HorizontalBarChart = ({ data }: HorizontalBarChartProps) => {
  return (
    <Box mt={6}>
      {data.map((item, index) => (
        <Flex align="center" mb={3}>
          {/* Label */}
          <Box w="220px">
            <Text fontSize="xs" color="gray.600">
              {item.label}
            </Text>
          </Box>

          {/* Barra */}
          <Box flex="1" bg="gray.100" h="24px" borderRadius="md">
            <Box
              h="100%"
              w={`${item.value}%`}
              bg="red.400"
              borderRadius="md"
              transition="0.3s"
            />
          </Box>

          {/* Valor */}
          <Box w="50px" textAlign="right">
            <Text fontSize="xs" color="gray.600">
              {item.value}%
            </Text>
          </Box>
        </Flex>
      ))}

      {/* Escala */}
      <Flex align="center" mt={2}>
        {/* Espaço do label */}
        <Box w="220px" />

        {/* Área da escala */}
        <Flex flex="1" justify="space-between">
          {[0, 25, 50, 75, 100].map(n => (
            <Text key={n} fontSize="xs" color="gray.500">
              {n}
            </Text>
          ))}
        </Flex>

        {/* Espaço do valor */}
        <Box w="50px" />
      </Flex>
    </Box>
  );
};

interface VerticalBarItem {
  label: string;
  value: number;
}

interface VerticalBarChartProps {
  data: VerticalBarItem[];
}

export const VerticalBarChart = ({ data }: VerticalBarChartProps) => {
  const max = 1500;
  const chartHeight = 220;

  const getColor = (value: number) => {
    if (value <= 1000) return '#4FD1C5';
    if (value <= 1200) return '#ECC94B';
    return '#F56565';
  };

  const yAxis = [0, 350, 700, 1050, 1400];

  return (
    <Box mt={6}>
      <Flex align="flex-end">
        {/* ✅ EIXO Y (vertical) */}
        <Flex
          direction="column"
          justify="space-between"
          h={`${chartHeight}px`}
          mr={3}
        >
          {yAxis.reverse().map(n => (
            <Text key={n} fontSize="xs" color="gray.500">
              {n}
            </Text>
          ))}
        </Flex>

        {/* ✅ GRÁFICO */}
        <Flex align="flex-end" h={`${chartHeight}px`} gap={4} flex="1">
          {data.map((item, index) => {
            const height = (item.value / max) * chartHeight;

            return (
              <Flex
                direction="column"
                align="center"
                justify="flex-end"
                flex="1"
              >
                <Box
                  w="100%"
                  h={`${height}px`}
                  bg={getColor(item.value)}
                  borderRadius="md"
                  transition="0.3s"
                />

                <Text mt={2} fontSize="xs" textAlign="center">
                  {item.label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Flex>

      {/* legenda */}
      <Box mt={4} p={3} borderRadius="md" fontSize="xs" color="gray.600">
        <Text fontWeight="bold">Referência:</Text> Até 1000 ppm = Adequado |
        1000-1500 ppm = Moderado | &gt;1500 ppm = Ruim
      </Box>
    </Box>
  );
};

export function DashboardModal({ onCancel }: Props) {
  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      w="100vw"
      h="100vh"
      bg="blackAlpha.600"
      align="center"
      justify="center"
      zIndex={999}
    >
      <Box
        bg="white"
        maxH="90vh"
        borderRadius="md"
        display="flex"
        flexDirection="column"
      >
        {/* HEADER FIXO */}
        <Flex
          justify="space-between"
          align="center"
          p={6}
          borderBottom="1px solid #eee"
        >
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Dashboard COPSOQ III
            </Text>
            <Text fontSize="sm" color="gray.500">
              Fatores Psicossociais do Trabalho - Total de Respondentes: 250
            </Text>
          </Box>

          <IconButton
            aria-label="Fechar"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onCancel}
          />
        </Flex>

        {/* CONTEÚDO COM SCROLL */}
        <Box overflowY="auto" p={6}>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              5.1. Demandas Psicológicas e Cognitivas
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              42% da população apresenta exposição alta a demandas psicológicas
              e cognitivas, indicando sobrecarga mental, pressão de tempo e
              exigências de concentração elevadas.
            </Text>{' '}
            <PieChart baixo={16.8} moderado={41.2} alto={42} />
          </Box>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              5.2. Controle e Autonomia{' '}
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              31,2% reporta baixo controle sobre o próprio trabalho, indicando
              limitações na autonomia, participação em decisões e
              previsibilidade das tarefas.{' '}
            </Text>{' '}
            <PieChart alto={23.2} moderado={45.6} baixo={31.2} />
          </Box>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              5.3. Suporte Social e Liderança
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              20,8% percebe baixo suporte social de colegas e líderes. Embora
              seja minoria, representa área de atenção para fortalecimento do
              clima organizacional.
            </Text>{' '}
            <PieChart alto={28.4} moderado={50.8} baixo={20.8} />
          </Box>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              5.4. Justiça Organizacional
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              27,2% percebe baixa justiça organizacional, indicando falta de
              transparência e equidade nas decisões e recompensas.
            </Text>{' '}
            <PieChart alto={26.8} moderado={46} baixo={27.2} />
          </Box>
        </Box>

        {/* FOOTER FIXO */}
        <Flex justify="flex-end" gap={3} p={6} borderTop="1px solid #eee">
          <Button onClick={onCancel} bg="teal.400" color="white">
            Fechar
          </Button>
          <Button onClick={onCancel} bg="red.400" color="white">
            Gerar Relatório
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}

export function DashboardRecModal({ onCancel }: Props) {
  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      w="100vw"
      h="100vh"
      bg="blackAlpha.600"
      align="center"
      justify="center"
      zIndex={999}
    >
      <Box
        bg="white"
        maxH="90vh"
        borderRadius="md"
        display="flex"
        flexDirection="column"
      >
        {/* HEADER FIXO */}
        <Flex
          justify="space-between"
          align="center"
          p={6}
          borderBottom="1px solid #eee"
        >
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Dashboard RED/TIC
            </Text>
            <Text fontSize="sm" color="gray.500">
              Riscos Psicossociais Associados a Tecnologias - Total de
              Respondentes: 250
            </Text>
          </Box>

          <IconButton
            aria-label="Fechar"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onCancel}
          />
        </Flex>

        {/* CONTEÚDO COM SCROLL */}
        <Box overflowY="auto" p={6}>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              6. Riscos Psicossociais Associados a Tecnologias (RED/TIC)
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              O RED/TIC avalia riscos específicos do uso intensivo de
              tecnologias da informação e comunicação.
            </Text>{' '}
            <PieChart baixo={16.8} moderado={41.2} alto={42} />
          </Box>

          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            <Box mt={6} mb={10}>
              <Text fontWeight="bold" mb={3}>
                Principais manifestações identificadas:
              </Text>

              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color="gray.700">
                  • Expectativa de resposta imediata a mensagens fora do horário{' '}
                  <Text as="span" fontWeight="bold">
                    (62%)
                  </Text>
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Dificuldade para desconectar ao final do expediente{' '}
                  <Text as="span" fontWeight="bold">
                    (58%)
                  </Text>
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Interrupções frequentes por notificações durante tarefas de
                  concentração{' '}
                  <Text as="span" fontWeight="bold">
                    (71%)
                  </Text>
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Reuniões online consecutivas sem intervalos adequados{' '}
                  <Text as="span" fontWeight="bold">
                    (54%)
                  </Text>
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Sensação de estar sempre disponível e acessível{' '}
                  <Text as="span" fontWeight="bold">
                    (66%)
                  </Text>
                </Text>
              </VStack>
            </Box>
            <HorizontalBarChart
              data={[
                { label: 'Interrupções por notificações', value: 71 },
                { label: 'Sensação de estar sempre disponível', value: 66 },
                { label: 'Expectativa de resposta imediata', value: 62 },
                { label: 'Dificuldade para desconectar', value: 58 },
                { label: 'Reuniões online sem intervalos', value: 54 },
              ]}
            />
          </Box>
        </Box>

        {/* FOOTER FIXO */}
        <Flex justify="flex-end" gap={3} p={6} borderTop="1px solid #eee">
          <Button onClick={onCancel} bg="teal.400" color="white">
            Fechar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}

export function DashboardArModal({ onCancel }: Props) {
  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      w="100vw"
      h="100vh"
      bg="blackAlpha.600"
      align="center"
      justify="center"
      zIndex={999}
    >
      <Box
        bg="white"
        maxH="90vh"
        borderRadius="md"
        display="flex"
        flexDirection="column"
      >
        {/* HEADER FIXO */}
        <Flex
          justify="space-between"
          align="center"
          p={6}
          borderBottom="1px solid #eee"
        >
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Dashboard Qualidade do Ar
            </Text>
            <Text fontSize="sm" color="gray.500">
              Avaliação da Qualidade do Ar nos Ambientes de Trabalho - Total de
              Respondentes: 250
            </Text>
          </Box>

          <IconButton
            aria-label="Fechar"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onCancel}
          />
        </Flex>

        {/* CONTEÚDO COM SCROLL */}
        <Box overflowY="auto" p={6}>
          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              7.1. Avaliação Geral da Qualidade do Ar
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              26% dos colaboradores avaliam a qualidade do ar como ruim,
              indicando necessidade de melhoria na ventilação e controle de
              poluentes internos.
            </Text>{' '}
            <PieChart baixo={16.8} moderado={41.2} alto={42} />
          </Box>

          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            <Text fontWeight="bold" mb={2}>
              {' '}
              7.2. Níveis de CO₂ por Área
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {`Salas de reunião apresentam concentração de CO₂ acima do recomendado (>1000 ppm), indicando ventilação insuficiente. O ideal é manter níveis abaixo de 1000 ppm.`}
            </Text>{' '}
            <VerticalBarChart
              data={[
                { label: 'Sala de Reunião A', value: 1300 },
                { label: 'Open Space 1', value: 900 },
                { label: 'Open Space 2', value: 1100 },
                { label: 'Sala de Reunião B', value: 1400 },
                { label: 'Copa', value: 800 },
                { label: 'Escritório Gerência', value: 900 },
              ]}
            />
          </Box>

          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            {' '}
            <Text fontWeight="bold" mb={2}>
              {' '}
              7.3. Material Particulado (PM2.5)
            </Text>{' '}
            <Text fontSize="sm" color="gray.600">
              {' '}
              16,8% dos ambientes apresentam níveis de material particulado
              acima do recomendado. Recomenda-se melhorias no sistema de
              filtragem de ar.
            </Text>{' '}
            <PieChart baixo={16.8} moderado={41.2} alto={42} />
          </Box>

          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            <Flex justifyContent="space-around">
              <Box>
                <Text fontWeight="bold" mb={2}>
                  {' '}
                  7.4. Temperatura
                </Text>{' '}
                <Text fontSize="sm" color="gray.600">
                  62,4% dos ambientes mantêm temperatura adequada entre 20-24°C.
                </Text>{' '}
                <PieChart baixo={16.8} moderado={41.2} alto={42} />
              </Box>
              <Box>
                {' '}
                <Text fontWeight="bold" mb={2}>
                  {' '}
                  7.5. Umidade Relativa
                </Text>{' '}
                <Text fontSize="sm" color="gray.600">
                  54,8% dos ambientes mantêm umidade adequada entre 40-60%.
                </Text>{' '}
                <PieChart baixo={16.8} moderado={41.2} alto={42} />
              </Box>
            </Flex>
          </Box>

          <Box bg="gray.50" p={5} mt={4} borderRadius="md">
            <Box mt={6} mb={10}>
              <Text fontWeight="bold" mb={3}>
                Principais Recomendações:
              </Text>

              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color="gray.700">
                  • Melhorar ventilação nas salas de reunião para reduzir
                  concentração de CO₂
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Implementar sistema de filtragem de ar mais eficiente para
                  controle de PM2.5
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Ajustar sistema de climatização em áreas com temperatura
                  inadequada
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Instalar umidificadores em ambientes com baixa umidade
                  relativa
                </Text>

                <Text fontSize="sm" color="gray.700">
                  • Realizar manutenção preventiva regular dos sistemas de HVAC
                </Text>
              </VStack>
            </Box>
          </Box>
        </Box>

        {/* FOOTER FIXO */}
        <Flex justify="flex-end" gap={3} p={6} borderTop="1px solid #eee">
          <Button onClick={onCancel} bg="teal.400" color="white">
            Fechar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}
